<template>
  <v-dialog v-model="show" max-width="600" persistent>
    <v-card class="amm-card">
      <!-- Cabecera azul -->
      <div class="amm-header">
        <div class="amm-header-left">
          <div class="amm-role-chip">
            <v-icon size="13" color="white">{{ subjectIcon }}</v-icon>
            <span>{{ subjectLabel }}</span>
          </div>
          <p class="amm-title">
            Revisión de tareas
            <span class="amm-count">· {{ tasks.length }} {{ tasks.length === 1 ? 'tarea' : 'tareas' }}</span>
          </p>
        </div>
        <v-btn icon dark x-small @click="$emit('close')" class="amm-close-btn">
          <v-icon size="18">mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="amm-body">
        <!-- Estado global -->
        <div class="amm-section-label">Estado para todas las tareas</div>
        <div class="amm-status-group">
          <button
            v-for="s in approvalStatuses"
            :key="s.value"
            class="amm-status-btn"
            :class="{
              'amm-status-btn--active': globalEstado === s.value,
              [`amm-status-btn--${s.value.toLowerCase()}`]: true
            }"
            :disabled="loading"
            type="button"
            @click="setGlobalEstado(s.value)"
          >
            <v-icon size="14" class="mr-1">{{ s.icon }}</v-icon>
            {{ s.text }}
          </button>
        </div>

        <!-- Comentario global -->
        <div class="amm-section-label mt-3">
          Comentario <span class="amm-optional">(opcional)</span>
        </div>
        <v-textarea
          v-model="globalComentario"
          placeholder="Agrega una observación para todas las tareas..."
          outlined
          dense
          hide-details
          :disabled="loading"
          rows="2"
          class="amm-textarea"
        ></v-textarea>

        <!-- Adjunto compartido -->
        <div class="amm-divider"></div>
        <div class="amm-section-label">Adjunto compartido <span class="amm-optional">(opcional, se aplica a todas)</span></div>
        <v-file-input
          v-model="sharedFile"
          @change="onSharedFileChange"
          :disabled="loading"
          outlined
          dense
          hide-details
          prepend-icon="mdi-paperclip"
          label="Seleccionar archivo"
          accept="application/pdf,image/*,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          :rules="fileRules"
          show-size
          clearable
          class="amm-file-input"
        ></v-file-input>

        <!-- Lista de tareas -->
        <div class="amm-divider"></div>
        <div class="amm-section-label">Tareas a revisar</div>

        <div class="amm-task-list">
          <div
            v-for="task in taskItems"
            :key="task.id"
            class="amm-task-row"
            :class="{ 'amm-task-row--expanded': task.expanded }"
          >
            <!-- Row principal -->
            <div class="amm-task-main">
              <v-checkbox
                v-model="task.included"
                :disabled="loading"
                hide-details
                dense
                class="amm-task-checkbox mt-0 pt-0"
                @change="onTaskIncludeChange(task)"
              ></v-checkbox>
              <span class="amm-task-name" :class="{ 'amm-task-name--excluded': !task.included }">
                {{ task.punto_control }}
              </span>
              <div class="amm-task-chips">
                <span class="amm-estado-chip" :class="`amm-estado-chip--${effectiveEstado(task).toLowerCase()}`">
                  {{ effectiveEstado(task) }}
                </span>
              </div>
              <v-btn
                icon
                x-small
                :disabled="loading || !task.included"
                @click="task.expanded = !task.expanded"
                class="amm-expand-btn"
              >
                <v-icon size="16">{{ task.expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
              </v-btn>
            </div>

            <!-- Override por tarea -->
            <v-expand-transition>
              <div v-if="task.expanded && task.included" class="amm-task-override">
                <div class="amm-section-label amm-section-label--sm">Sobrescribir estado</div>
                <div class="amm-status-group amm-status-group--sm">
                  <button
                    v-for="s in approvalStatuses"
                    :key="s.value"
                    class="amm-status-btn amm-status-btn--sm"
                    :class="{
                      'amm-status-btn--active': task.overrideEstado === s.value,
                      [`amm-status-btn--${s.value.toLowerCase()}`]: true
                    }"
                    :disabled="loading"
                    type="button"
                    @click="task.overrideEstado = task.overrideEstado === s.value ? null : s.value"
                  >
                    <v-icon size="12" class="mr-1">{{ s.icon }}</v-icon>
                    {{ s.text }}
                  </button>
                </div>
                <v-textarea
                  v-model="task.overrideComentario"
                  placeholder="Comentario específico (opcional)..."
                  outlined
                  dense
                  hide-details
                  :disabled="loading"
                  rows="2"
                  class="amm-textarea mt-2"
                ></v-textarea>
              </div>
            </v-expand-transition>
          </div>
        </div>
      </v-card-text>

      <!-- Acciones -->
      <div class="amm-actions">
        <span class="amm-included-count">
          {{ includedCount }} de {{ tasks.length }} tareas seleccionadas
        </span>
        <v-spacer></v-spacer>
        <v-btn text small :disabled="loading" color="grey darken-1" @click="$emit('close')">Cancelar</v-btn>
        <v-btn
          small
          depressed
          color="#2D4B95"
          dark
          :loading="loading"
          :disabled="includedCount === 0"
          @click="saveAll"
        >Guardar revisión</v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { bulkSetTaskApprovals } from '@/helpers/api/entrega';
import EventBus from '@/helpers/event_bus'
import { v4 as uuidv4 } from 'uuid'
import _debounce from 'lodash/debounce'
import {create as createDraftAPI, update as updateDraftAPI} from '@/helpers/api/drafts'

export default {
  name: 'AprobacionMasivaModal',

  props: {
    show: {
      type: Boolean,
      default: false
    },
    tasks: {
      type: Array,
      default: () => []
    },
    entregaId: {
      type: Number,
      default: null
    },
    userSubject: {
      type: Object,
      default: null
    },
    isITO: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      loading: false,
      globalEstado: 'Aprobado',
      globalComentario: '',
      sharedFile: null,
      // Draft/upload handling
      draftId: null,
      draftCreated: false,
      draftUpdateInterval: 20,
      draftSaveTimer: null,
      filesPreview: [],
      handleFormChangeDebounced: null,
      taskItems: [],
      approvalStatuses: [
        { text: 'Aprobado',  value: 'Aprobado',  icon: 'mdi-check-circle-outline'  },
        { text: 'Observado', value: 'Observado', icon: 'mdi-circle-half-full'       },
        { text: 'Rechazado', value: 'Rechazado', icon: 'mdi-close-circle-outline'   },
      ],
      fileRules: [
        v => !v || v.size < 10000000 || 'El archivo debe ser menor a 10 MB',
      ],
    };
  },

  computed: {
    subjectLabel() {
      return this.userSubject?.name ?? (this.isITO ? 'ITO' : 'Revisor');
    },
    subjectIcon() {
      return this.isITO ? 'mdi-shield-check' : 'mdi-account-hard-hat';
    },
    includedCount() {
      return this.taskItems.filter(t => t.included).length;
    }
    ,
    draftToLoad() { return this.$store.entregaStore.state.draftToLoad }
  },

  watch: {
    show(val) {
      if (val) {
        this.initTaskItems();
        this.setupUploadListener();
        this.globalEstado    = 'Aprobado';
        this.globalComentario = '';
        this.sharedFile      = null;
        this.filesPreview    = [];
        this.draftId = null;
        this.draftCreated = false;
        // setup autosave/listener
        this.handleFormChangeDebounced = _debounce(() => this.checkAndCreateDraft(), 1000)
        // draft routing is handled centrally by EntregaModal; do not auto-load here
      } else {
        // clear timers/listeners when modal closed
        if (this.draftSaveTimer) { clearInterval(this.draftSaveTimer); this.draftSaveTimer = null }
        this.removeUploadListener()
        if (this.handleFormChangeDebounced && this.handleFormChangeDebounced.cancel) {
          try { this.handleFormChangeDebounced.cancel() } catch (e) { console.log('Error cancelling debounce:', e) }
        }
        this.handleFormChangeDebounced = null
        this.draftId = null; this.draftCreated = false; this.filesPreview = []; this.sharedFile = null
      }
    },
    globalComentario() {
      if (this.handleFormChangeDebounced) this.handleFormChangeDebounced()
    },
    tasks() {
      if (this.show) {
        this.initTaskItems();
      }
    }
  },

  mounted() {
    // The dialog can be mounted while already open; in that case the `show`
    // watcher does not necessarily run because it is not immediate.
    if (this.show) {
      this.initTaskItems();
      this.setupUploadListener();
      this.handleFormChangeDebounced = _debounce(() => this.checkAndCreateDraft(), 1000)
    }
  },

  beforeDestroy() {
    this.removeUploadListener()
    if (this.draftSaveTimer) {
      clearInterval(this.draftSaveTimer)
      this.draftSaveTimer = null
    }
  },

  methods: {
    initTaskItems() {
      this.taskItems = this.tasks.map(t => ({
        id:                t.id,
        punto_control:     t.punto_control || t.name || `Tarea #${t.id}`,
        included:          true,
        expanded:          false,
        overrideEstado:    null,
        overrideComentario: '',
      }));
    },

    setupUploadListener() {
      // Make registration idempotent when the modal is reopened or tasks change.
      EventBus.$off('completedFileUpload', this.handleCompletedFileUpload)
      EventBus.$on('completedFileUpload', this.handleCompletedFileUpload)
    },

    removeUploadListener() {
      EventBus.$off('completedFileUpload', this.handleCompletedFileUpload)
    },

    handleCompletedFileUpload(payload) {
      if (!payload) return
      if (payload.uuid) {
        const idx = this.filesPreview.findIndex(f => f.uuid === payload.uuid)
        if (idx !== -1) {
          this.$set(this.filesPreview[idx], 'id', payload.file.id)
          if (payload.file.pathmedia && payload.file.pathmedia.path) this.$set(this.filesPreview[idx], 'src', payload.file.pathmedia.path)
        }
      }
    },

    setGlobalEstado(value) {
      this.globalEstado = value;
    },

    effectiveEstado(task) {
      if (!task.included) return '—';
      return task.overrideEstado ?? this.globalEstado;
    },

    onTaskIncludeChange(task) {
      if (!task.included) {
        task.expanded         = false;
        task.overrideEstado   = null;
        task.overrideComentario = '';
      }
    },

    async saveAll() {
      if (this.includedCount === 0) return;

      this.loading = true;

      try {
        const approvals = this.taskItems
          .filter(t => t.included)
          .map(t => ({
            task_id:    t.id,
            subject_id: this.userSubject?.id ?? null,
            estado:     t.overrideEstado ?? this.globalEstado,
            comentario: (t.overrideComentario?.trim() || this.globalComentario?.trim()) || null,
          }));

        const formData = new FormData();
        formData.append('entrega_id', this.entregaId);
        formData.append('approvals', JSON.stringify(approvals));
        // If we used draft uploads, send draft_id so backend reassigns files.
        if (this.draftId) formData.append('draft_id', this.draftId)
        // If user removed shared attachment
        if (!this.filesPreview.length && !this.sharedFile) formData.append('remove_attachment', '1')

        await bulkSetTaskApprovals(formData);

        this.$emit('saved');
        this.$emit('close', true);
      } catch (err) {
        console.error('bulkSetTaskApprovals error', err);
      } finally {
        this.loading = false;
      }
    }
,
    // Draft helpers
    checkAndCreateDraft() { if (!this.draftCreated && !this.draftId) this.createDraft() },

    async createDraft(force_create = false) {
      if (this.draftCreated || this.draftId) return null
      if ((!this.globalComentario || this.globalComentario.trim() === '') && !force_create) return null
      this.draftCreated = true
			const formDraft = {
				comentario: this.globalComentario,
				estado: this.globalEstado,
				entregaId: this.entregaId,
        approval: this.taskItems
          .filter(t => t.included)
          .map(t => ({
            task_id:    t.id,
            subject_id: this.userSubject?.id ?? null,
            estado:     t.overrideEstado ?? this.globalEstado,
            comentario: (t.overrideComentario?.trim() || this.globalComentario?.trim()) || null,
          }))
			}
      const draftData = {
        type: 12,
        project_id: this.$store.projectsStore.state.selectedProject.id,
        type_id: this.entregaId,
        data: JSON.stringify(formDraft)
      }
      try {
        const response = await createDraftAPI(draftData)
        if (response.code === 200) {
          this.draftId = response.draft.id
          this.startDraftUpdateTimer()
          EventBus.$emit('reloadDraftList')
          return this.draftId
        }
        return null
      } catch (error) { this.draftCreated = false; throw error }
    },

    startDraftUpdateTimer() {
      if (this.draftSaveTimer) { clearInterval(this.draftSaveTimer); this.draftSaveTimer = null }
      this.draftSaveTimer = setInterval(() => { this.updateDraft() }, this.draftUpdateInterval * 1000)
    },

    async updateDraft() {
      if (!this.draftId) return
			const formDraft = {
				comentario: this.globalComentario,
				estado: this.globalEstado,
				entregaId: this.entregaId,
        approval: this.taskItems
          .filter(t => t.included)
          .map(t => ({
            task_id:    t.id,
            subject_id: this.userSubject?.id ?? null,
            estado:     t.overrideEstado ?? this.globalEstado,
            comentario: (t.overrideComentario?.trim() || this.globalComentario?.trim()) || null,
          }))
			}
      await updateDraftAPI({ id: this.draftId, type: 12, project_id: this.$store.projectsStore.state.selectedProject.id, type_id: this.entregaId, data: JSON.stringify(formDraft) })
    },

    async loadDraft(draft) {
      if (draft && draft.data) {
        const data = (typeof draft.data === 'string') ? JSON.parse(draft.data) : draft.data
        // merge draft fields into current state
        this.globalComentario = data.comentario ?? this.globalComentario
        this.globalEstado = data.estado ?? this.globalEstado

        // apply approvals array to taskItems when present
        try {
          const approvals = Array.isArray(data.approval) ? data.approval : (Array.isArray(data.approvals) ? data.approvals : null)
          if (approvals) {
            // ensure taskItems initialized
            if (!this.taskItems || !this.taskItems.length) this.initTaskItems()
            const map = {}
            approvals.forEach(a => { const id = Number(a.task_id ?? a.taskId); if (!isNaN(id)) map[id] = a })
            this.taskItems.forEach((t, idx) => {
              const a = map[Number(t.id)]
              if (a) {
                this.$set(this.taskItems[idx], 'included', true)
                this.$set(this.taskItems[idx], 'overrideEstado', a.estado ?? null)
                this.$set(this.taskItems[idx], 'overrideComentario', a.comentario ?? '')
              } else {
                this.$set(this.taskItems[idx], 'included', false)
                this.$set(this.taskItems[idx], 'overrideEstado', null)
                this.$set(this.taskItems[idx], 'overrideComentario', '')
              }
            })
          }
        } catch (e) { console.warn('loadDraft apply approvals failed', e) }

        const filePreviews = []
        if (draft.files && Array.isArray(draft.files)) {
          for (const file of draft.files) {
            if (file.mime_type && file.mime_type.includes('image') && file.pathmedia?.path) {
              filePreviews.push({ src: file.pathmedia.path, id: file.id, name: file.name })
            } else {
              filePreviews.push({ noImage: true, id: file.id, name: file.name })
            }
          }
        }
        this.filesPreview = filePreviews
        this.draftId = draft.id
        this.draftCreated = true
        this.startDraftUpdateTimer()
        this.$store.globalStore.dispatch('addMessage', {
          message: 'Borrador cargado correctamente.', title: 'Borrador Cargado',
          created: new Date(), type: 'success', icon: 'mdi-check-circle', color: '#3EBFA3'
        })
      }
    },

    async onSharedFileChange(file) {
      const selected = file
      if (!selected) return
      if (selected.size >= 10000000) {
        // respect fileRules validation handled by v-file-input
        this.sharedFile = null
        return
      }
      // ensure draft exists
      if (!this.draftId && !this.draftCreated) await this.createDraft(true)
      const uuid = uuidv4()
      if (selected.type && selected.type.startsWith('image/')) {
        const b64 = await this.toBase64(selected)
        this.filesPreview = [{ src: b64, name: selected.name, uuid }]
      } else {
        this.filesPreview = [{ noImage: true, name: selected.name, uuid }]
      }
      this.$store.globalStore.commit('setFilesToUploadCenter', {
        type: 13,
        payload: { draft_id: this.draftId, project_id: this.$store.projectsStore.state.selectedProject.id, uuid },
        description: 'Aprobación masiva', total: selected.size, uuid, file: selected
      })
    },

    toBase64(file) {
      return new Promise(resolve => {
        const r = new FileReader(); r.readAsDataURL(file)
        r.onload = () => resolve(r.result)
        r.onerror = () => resolve(null)
      })
    },
  }
};
</script>

<style scoped>
.amm-card {
  border-radius: 10px !important;
  overflow: hidden;
}

/* Header */
.amm-header {
  background: #2D4B95;
  padding: 12px 14px 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.amm-header-left {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.amm-role-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: rgba(255,255,255,0.18);
  border-radius: 20px;
  padding: 2px 8px;
  font-size: 11px;
  color: #fff;
  font-weight: 500;
  width: fit-content;
}
.amm-title {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  line-height: 1.3;
}
.amm-count {
  font-weight: 400;
  opacity: 0.82;
}
.amm-close-btn {
  align-self: flex-start;
}

/* Body */
.amm-body {
  padding: 14px 16px 8px !important;
  max-height: 65vh;
  overflow-y: auto;
}

/* Section labels */
.amm-section-label {
  font-size: 11px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 6px;
}
.amm-section-label--sm {
  font-size: 10px;
  margin-bottom: 4px;
}
.amm-optional {
  text-transform: none;
  font-weight: 400;
  color: #999;
  letter-spacing: 0;
}

/* Status pills */
.amm-status-group {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}
.amm-status-group--sm {
  gap: 4px;
}
.amm-status-btn {
  border: 1px solid #ccc;
  border-radius: 20px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  background: #fff;
  color: #555;
  display: inline-flex;
  align-items: center;
  transition: all 0.15s;
  outline: none;
}
.amm-status-btn--sm {
  padding: 3px 8px;
  font-size: 11px;
}
.amm-status-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.amm-status-btn--active.amm-status-btn--aprobado  { background: #E8F5E9; border-color: #43A047; color: #2E7D32; }
.amm-status-btn--active.amm-status-btn--observado { background: #FFF8E1; border-color: #FFA000; color: #E65100; }
.amm-status-btn--active.amm-status-btn--rechazado { background: #FFEBEE; border-color: #E53935; color: #B71C1C; }

/* Textarea */
.amm-textarea >>> .v-input__slot { font-size: 13px; }

/* File input */
.amm-file-input { font-size: 13px; }

/* Divider */
.amm-divider {
  border-top: 1px solid #eee;
  margin: 12px 0;
}

/* Task list */
.amm-task-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.amm-task-row {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  background: #fafafa;
}
.amm-task-row--expanded {
  border-color: #2D4B95;
}
.amm-task-main {
  display: flex;
  align-items: center;
  padding: 6px 8px 6px 4px;
  gap: 6px;
}
.amm-task-checkbox {
  flex-shrink: 0;
}
.amm-task-name {
  flex: 1;
  font-size: 12px;
  color: #333;
  font-weight: 500;
}
.amm-task-name--excluded {
  opacity: 0.4;
  text-decoration: line-through;
}
.amm-task-chips {
  display: flex;
  gap: 4px;
}
.amm-expand-btn {
  flex-shrink: 0;
}

/* Estado chips inline */
.amm-estado-chip {
  font-size: 10px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 12px;
  white-space: nowrap;
  background: #eee;
  color: #666;
}
.amm-estado-chip--aprobado  { background: #E8F5E9; color: #2E7D32; }
.amm-estado-chip--observado { background: #FFF8E1; color: #E65100; }
.amm-estado-chip--rechazado { background: #FFEBEE; color: #B71C1C; }

/* Override section */
.amm-task-override {
  padding: 8px 12px 10px;
  border-top: 1px solid #e8edf8;
  background: #f0f4ff;
}

/* Actions footer */
.amm-actions {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  border-top: 1px solid #eee;
  gap: 8px;
}
.amm-included-count {
  font-size: 11px;
  color: #888;
}
</style>
